import { defineComponent, reactive, SetupContext, readonly, ref, onMounted, toRefs, watch } from '@vue/composition-api';
import CommonCategory from '@/components/common/common-content-category.component.vue';         // カテゴリ選択共通コンポーネント
import vueRouter from '@/router/index';
import SystemConst from '@/shared/system-const';
import RichTextEditor from '../common/rich-text-editor.component.vue';
import { ContentDeleteType, AttachmentFile } from '@/shared/type';
import CommonMessageDialog from '@/components/common/common-message-dialog.component.vue';
import CommonFunction from '@/shared/common-function';
import MessageConst from '@/shared/message-const';
import HttpStatus from 'http-status-codes';

// リアクティブな変数の型定義
export type reactiveData = {
    windowSize: { x: number; y: number };                     // Windowサイズ
    content: ContentDeleteType;                               // 教育用コンテンツフォーム
    createMethodDisplay: string;                              // 作成形式（表示用）
    filesDisplay: AttachmentFile[];                           // 添付ファイル（表示用）
}

/**
 * 教育用コンテンツ削除画面コンポーネント
 */
export default defineComponent({
    components: { RichTextEditor, CommonCategory, CommonMessageDialog },
    setup(props, ctx: SetupContext) {
        const formHeight = SystemConst.FORM_HEIGHT.CONTENT;   // フォーム高さ調整用変数
        const form = ref<any>(null);                          // 入力フォーム
        const categoryRef = ref<any>(null);                   // カテゴリー選択
        const router = vueRouter;                             // ルータ
        const commonRef = ref<any>(null);                     // メッセージダイアログ
        const createStyleSet = {
            // 作成形式（選択）の値
            simple: SystemConst.CREATE_METHOD_TEXT.SIMPLE,    // 簡易登録
            link: SystemConst.CREATE_METHOD_TEXT.LINK         // リンクURL登録
        };
        // リアクティブな変数定義
        const state: reactiveData = reactive<reactiveData>({
            windowSize: { x: 0, y: 0 },                       // ウィンドウサイズ
            content: {
                title: '',
                publicationRangeName: '',
                createMethodName: SystemConst.CREATE_METHOD_TEXT.SIMPLE,
                linkUrl: '',
                body: '',
                referenceUrl: '',
                files: []
            },
            createMethodDisplay: '',                          // 作成形式（表示用）
            filesDisplay: []                                  // 添付ファイル（表示用）
        });

        let contentId = '';                                   // 削除対象のコンテンツID

        // カテゴリ選択のアクティブノードを監視する
        const { activeNode } = toRefs(ctx.root.$store.state.content);
        // カテゴリが選択された場合
        watch(activeNode, () => {
            // selectCategory()を呼び出し、教育用コンテンツ一覧画面に遷移する
            selectCategory();
        })

        // 表示内容の設定
        onMounted(() => {
            // カテゴリデータデータの取得関数呼び出し
            getCategory();

            init();
        });

        /**
         * カテゴリデータの取得
         */
        async function getCategory() {
            // カテゴリ取得
            categoryRef.value.getCategory();
        }

        async function init() {
            // 教育用コンテンツ更新/削除対象取得APIで初期表示データを取得する
            // パラメータ:コンテンツID
            const response = await ctx.root.$store.dispatch('content/getOperateContent',
                {
                    contentId: contentId
                }
            );

            // HTTPステータスコードが200以外の場合
            if (response.status !== HttpStatus.OK) {
                // HTTPステータスコードが200以外の場合
                if (response.status === HttpStatus.NOT_FOUND) {
                    /// HTTPステータスコードが404の場合、displayErrorMessageTransitionDialogメソッドで
                    // 教育用コンテンツ一覧画面へ戻る
                    commonRef.value.displayErrorMessageTransitionDialog(response, '/content/list');
                } else {
                    // HTTPステータスコードが200, 404以外の場合
                    // errorDisplayTransitionControlメソッドでエラー画面へ遷移して処理を終了する
                    commonRef.value.errorDisplayTransitionControl(response);
                }
                return;
            }

            // HTTPステータスコードが200の場合
            // 取得したデータを画面へ設定する
            state.content = response.data.content;

            // 作成形式を作成形式（表示用）に設定する
            state.createMethodDisplay = state.content.createMethodName;

            // 作成方式が'簡易登録'で、添付ファイルを取得している場合
            if (state.content.createMethodName
                === createStyleSet['simple'] && state.content.files.length !== 0) {
                // 添付ファイルのサイズを「B(バイト)」表記から「KB(キロバイト)」表記へ変換する
                state.content.files.forEach(element => {
                    const tmpFiles: AttachmentFile[] = [         // 一時保存する添付ファイル情報
                        {
                            fileIcon: '',
                            fileSequence: 0,
                            fileName: '',
                            fileSize: ''
                        }
                    ]
                    // KBへ変換し、小数第三位まで表示する
                    const conversionFileSize = String(Math.round((element.fileSize / 1024) * 1000) / 1000);
                    tmpFiles[0].fileName = element.fileName;
                    tmpFiles[0].fileSize = conversionFileSize;
                    // 添付ファイル（表示用）に設定する
                    state.filesDisplay.push(tmpFiles[0]);
                });
            }
        }

        /**
         * 初期表示
         */
        created();
        async function created() {
            // コンテンツIDの取得
            contentId = ctx.root.$route.params.id;

            // titleIcon変更
            ctx.root.$store.commit('setTitleIcon', 'mdi-text-box-remove-outline');

            // mainTitleを「教育用コンテンツ削除」に変更
            ctx.root.$store.commit('setMainTitle', '教育用コンテンツ削除');
        }

        /**
         * 削除ボタン押下時に確認メッセージダイアログ表示
         */
        function displayDialog() {
            // 確認メッセージダイアログ表示
            const displayMessage = CommonFunction.replaceArray(MessageConst.S_CONFIRMATION, ['削除']);
            commonRef.value.displayConfirmationMessageDialog(displayMessage);
        }

        /**
         * 削除画面にて、確認メッセージダイアログの「はい」を押下時の処理
         */
        async function processingData() {
            // 教育用コンテンツ削除APIを呼び出す
            // パラメータ: コンテンツID
            const response = await ctx.root.$store.dispatch('content/deleteContent',
                {
                    'contentId': contentId
                }
            );

            // 通信結果判別
            if (response.status !== HttpStatus.OK) {
                // HTTPステータスコードが200以外の場合
                if (response.status === HttpStatus.NOT_FOUND) {
                    //  HTTPステータスコードが404の場合
                    //  displayErrorMessageTransitionDialogメソッドで教育用コンテンツ一覧画面に遷移
                    commonRef.value.displayErrorMessageTransitionDialog(response, '/content/list');
                } else {
                    //  HTTPステータスコードが404以外の場合
                    //  errorDisplayTransitionControlメソッドでエラー画面を表示する
                    commonRef.value.errorDisplayTransitionControl(response);
                }
                return;
            }

            // HTTPステータスコードが200の場合
            //  削除完了メッセージダイアログを表示して教育用コンテンツ一覧画面に遷移
            commonRef.value.displayMessageTransitionDialog(response, '/content/list');
        }

        /**
         * キャンセルボタン押下時の処理
         */
        function back() {
            // storeのページの最終データフラグをfalseに設定
            ctx.root.$store.commit('content/setIsPageLastData', false);

            router.push('/content/list');
        }

        /**
         * カテゴリ選択時のイベント
         */
        function selectCategory() {
            // ストアのページ情報をクリア
            ctx.root.$store.commit('content/clearStorePage');
            // 教育用コンテンツ一覧画面へ遷移
            router.push('/content/list');
        }

        /**
         * ウィンドウサイズ変更監視
         * 変更されたウィンドウサイズを変数に保持する
         */
        function onResize(): void {
            state.windowSize = { x: window.innerWidth, y: window.innerHeight };
            // カテゴリー選択コンポーネントのサイズも変更
            categoryRef.value.resize(state.windowSize);
        }

        return {
            state: readonly(state),
            formHeight,
            form,
            categoryRef,
            commonRef,
            createStyleSet,
            displayDialog,
            processingData,
            back,
            onResize
        };
    }
});
